import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  ButtonSize,
  ButtonVariant,
  FlyoutContainer,
  Link,
  CmhcIcon,
  CmhcIconSize
} from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { useTranslation } from 'react-i18next';
import { PrefixFieldFolder, SharepointDomain, Success, UnSuccess } from '../../../../constants';
import { getApplicationErrorReport } from '../../../HbtPortfolioManagementPage/ValidationInventoryService';
import styles from './styles.module.scss';

const normalizeExcelFileName = (fileName?: string) => {
  if (!fileName) {
    return '';
  }

  if (/\.(xls|xlsm|xlsx)$/i.test(fileName)) {
    return fileName.replace(/\.(xls|xlsm|xlsx)$/i, '.xlsx');
  }

  return fileName;
};

type DocumentsResourcesFlyoutProps = {
  fileId: string;
  fieldName: string;
  applicationId?: number;
};

const DocumentsResourcesFlyout = ({
  fileId,
  fieldName,
  applicationId
}: DocumentsResourcesFlyoutProps) => {
  const [openFlyout, setOpenFlyout] = useState<boolean>(false);
  const [appId, setApplicationId] = useState(applicationId);
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const env = encodeURIComponent(`${process.env.REACT_APP_ENV}`);
  // sending as true for now till we finalize the solution for getting sucessfull and unsucessfull
  const isSuccess = true;
  const normalizedFileId = normalizeExcelFileName(fileId);

  useEffect(() => {
    setApplicationId(applicationId);
  }, [applicationId]);

  const toggleDrawer = (newOpen: boolean) => {
    setOpenFlyout(newOpen);
  };

  const authenticationContext = useAuthenticationContext();

  const handleGetErrorReport = async (applicationId?: number) => {
    if (!applicationId) return;
    const errorReport = await getApplicationErrorReport(authenticationContext, applicationId);
    if (!errorReport) return;

    const dispo = errorReport.headers['content-disposition'] ?? '';
    const match = dispo.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] ?? 'data.xlsx';

    const url = URL.createObjectURL(errorReport.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <Button
        type="button"
        name="documents_resources"
        size={ButtonSize.SMALL}
        variant={ButtonVariant.TERTIARY}
        leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_support" />}
        text={{ value: t('PI-Portfolio-Management-Request-Overview-Documents-Resources') }}
        ariaText={{
          value: t('PI-Portfolio-Management-Request-Overview-Documents-Resources')
        }}
        onClick={() => toggleDrawer(true)}
      />
      <FlyoutContainer
        id={styles.documentsResourcesFlyoutContainer}
        isHeaderRequired={true}
        headerText={t('PI-Portfolio-Management-Request-Overview-Documents-Resources')}
        open={openFlyout}
        onClose={() => {
          toggleDrawer(false);
        }}
        content={
          <Box className={styles.documentsResourcesflyoutContainer} role="presentation">
            <Accordion
              accordionData={{
                heading: t('PI-Portfolio-Management-Request-Overview-Documents'),
                defaultExpanded: true,
                contentSlot: (
                  <Box className={styles.documentsResourcesflyoutLinksContainer}>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        aria-label={fieldName}
                        href={`${SharepointDomain}/${env}/${
                          isSuccess ? Success : UnSuccess
                        }/${normalizedFileId}`}
                        data-testid="document-resource-link"
                      >
                        {fieldName ? `PIRF: ${fieldName}` : ''}
                      </Link>
                    </Box>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Button
                        type="button"
                        name="documents_resources"
                        className={styles.errorReportButton}
                        size={ButtonSize.SMALL}
                        variant={ButtonVariant.TERTIARY}
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        text={{
                          value: t('View-Error-Report')
                        }}
                        ariaText={{
                          value: t('View-Error-Report')
                        }}
                        onClick={() => handleGetErrorReport(appId)}
                        data-testid="error-report-button"
                      />
                    </Box>
                  </Box>
                )
              }}
            ></Accordion>
            <Accordion
              accordionData={{
                heading: t('PI-Portfolio-Management-Request-Overview-Resources'),
                defaultExpanded: true,
                contentSlot: (
                  <Box className={styles.documentsResourcesflyoutLinksContainer}>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_pdf" />}
                        aria-label={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-User-Guide'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-User-Guide-Link'
                        )}
                        data-testid="user-guide-link"
                      >
                        {t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-User-Guide'
                        )}
                      </Link>
                    </Box>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_file_word" />
                        )}
                        aria-label={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Procedures'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Procedures-Link'
                        )}
                        data-testid="resource-link-procedures"
                      >
                        {t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Procedures'
                        )}
                      </Link>
                    </Box>

                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        aria-label={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Rules'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Rules-Link'
                        )}
                        data-testid="resource-link-rules"
                      >
                        {t('PI-Portfolio-Management-Request-Overview-Insurance-Processing-Rules')}
                      </Link>
                    </Box>
                    <Box className={styles.documentsResourcesWrapper}>
                      <Link
                        target="_blank"
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_msexcel" />
                        )}
                        aria-label={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Tracker'
                        )}
                        href={t(
                          'PI-Portfolio-Management-Request-Overview-Insurance-Processing-Tracker-Link'
                        )}
                        data-testid="resource-link-tracker"
                      >
                        {t('PI-Portfolio-Management-Request-Overview-Insurance-Processing-Tracker')}
                      </Link>
                    </Box>
                  </Box>
                )
              }}
            ></Accordion>
          </Box>
        }
      />
    </>
  );
};

export default DocumentsResourcesFlyout;
